'use client';

import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { ArrowRightLeft, Loader2 } from 'lucide-react';
import { formatUSD, truncateAddress, formatTimeAgo, cn } from '@/lib/utils';
import { generateGridAvatarUrl } from '@/lib/avatar';
import type { V2GridSeatEvent } from '@/lib/types/v2';
import { V2_CONSTANTS } from '@/lib/types/v2';
import { type TaxTokenConfig, formatTaxAmount } from '@/lib/drawer-utils';

interface SalesHistoryTableProps {
  events: V2GridSeatEvent[];
  pageSize?: number;
  taxTokenConfig?: TaxTokenConfig;
}

const SALE_EVENT_TYPES = new Set([
  'BOUGHT_VACANT',
  'BOUGHT_FROM_CREATOR',
  'BOUGHT_OUT',
  'YIELD_CLAIMED',
]);

const EVENT_LABELS: Record<string, { label: string; color: string }> = {
  BOUGHT_VACANT: { label: 'Claimed', color: 'text-blue-400' },
  BOUGHT_FROM_CREATOR: { label: 'Buyout', color: 'text-amber-400' },
  BOUGHT_OUT: { label: 'Buyout', color: 'text-amber-400' },
  YIELD_CLAIMED: { label: 'Yield', color: 'text-win-green' },
};

export function SalesHistoryTable({ events, pageSize = 20, taxTokenConfig }: SalesHistoryTableProps) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const salesEvents = useMemo(() => {
    return events.filter(e => SALE_EVENT_TYPES.has(e.eventType));
  }, [events]);

  const visibleEvents = salesEvents.slice(0, visibleCount);
  const hasMore = visibleCount < salesEvents.length;

  const loadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + pageSize, salesEvents.length));
  }, [pageSize, salesEvents.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: '100px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  if (salesEvents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <ArrowRightLeft className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No sales yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {/* Header */}
      <div className="grid grid-cols-[auto,1fr,auto,auto] gap-2 px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        <span className="w-14">Type</span>
        <span>User</span>
        <span className="text-right w-16">Price</span>
        <span className="text-right w-12">When</span>
      </div>

      <div className="max-h-[320px] overflow-y-auto space-y-0.5 scrollbar-thin">
        {visibleEvents.map((event) => {
          const meta = EVENT_LABELS[event.eventType] || { label: event.eventType, color: 'text-muted-foreground' };
          const price = event.price || event.amount;
          const priceBigint = price ? BigInt(price) : 0n;
          const seatNum = event.seatId ? event.seatId.split('-').pop() : undefined;

          return (
            <div
              key={event.id}
              className="grid grid-cols-[auto,1fr,auto,auto] gap-2 items-center px-2 py-1.5 rounded-md hover:bg-muted/30 transition-colors text-sm"
            >
              <span className={cn('text-xs font-medium w-14 truncate', meta.color)}>
                {meta.label}
                {seatNum !== undefined && (
                  <span className="text-muted-foreground ml-0.5">#{seatNum}</span>
                )}
              </span>
              <div className="flex items-center gap-1.5 min-w-0">
                <img
                  src={event.userIdentity?.avatarUrl || generateGridAvatarUrl(event.user, 16)}
                  alt=""
                  className="w-4 h-4 rounded-sm shrink-0"
                />
                <span className="text-xs truncate text-muted-foreground">
                  {event.userIdentity?.displayName || truncateAddress(event.user, 3)}
                </span>
              </div>
              <span className="text-xs font-mono text-right w-16">
                {priceBigint > 0n ? (taxTokenConfig ? formatTaxAmount(priceBigint, taxTokenConfig) : formatUSD(priceBigint)) : '—'}
              </span>
              <div className="flex items-center gap-1 justify-end w-12">
                <span className="text-[10px] text-muted-foreground">
                  {formatTimeAgo(event.timestamp * 1000)}
                </span>
              </div>
            </div>
          );
        })}

        {/* Infinite scroll sentinel */}
        {hasMore && (
          <div ref={sentinelRef} className="flex items-center justify-center py-3">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground text-center pt-1">
        {visibleEvents.length} of {salesEvents.length} events
      </p>
    </div>
  );
}
